<template>
  <b-card>
    <b-tabs>
      <b-tab
        :title="$t('Branding & Official Stamp')"
        active
      >
        <validation-observer ref="uploadRules">
          <b-form>
            <h5 class="section-label mb-1">
              {{ $t('Color Palette & Stamp') }}
            </h5>
            <b-row class="match-height mb-1">
              <b-col md="6">
                <InputField
                  id="theme-primary-color"
                  v-model="imageFormData.primaryColor"
                  :label="$t('Theme Primary Color')"
                  :placeholder="$t('Enter theme primary color')"
                  type="color"
                  for="theme-primary-color"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="6">
                <InputField
                  id="theme-secondary-color"
                  v-model="imageFormData.secondaryColor"
                  :label="$t('Theme Secondary Color')"
                  :placeholder="$t('Enter theme secondary color')"
                  type="color"
                  for="theme-secondary-color"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>

            <h5 class="section-label mb-1">
              {{ $t('Font') }}
            </h5>
            <b-row>
              <b-col md="6">
                <SelectField
                  id="preferredFont"
                  v-model="configurationFormData.fontFamily"
                  :label="$t('Preferred Fonts')"
                  :reduce="(option) => option.value"
                  :options="preferredFonts"
                  item="label"
                  :placeholder="$t('Select preferred font')"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>

            <h5 class="section-label mb-1 mt-1">
              {{ $t('Session Settings') }}
            </h5>
            <b-row>
              <b-col md="6">
                <InputField
                  id="session-timeout"
                  v-model="configurationFormData.sessionTimeout"
                  :label="$t('Session Timeout (minutes)')"
                  :placeholder="$t('Enter session timeout in minutes')"
                  type="number"
                  :readonly="isReadonly"
                  :min="1"
                  :max="60"
                  @input="validateSessionTimeout"
                />
                <small
                  v-if="sessionTimeoutError"
                  class="text-danger error-message"
                >
                  {{ sessionTimeoutError }}
                </small>
                <small
                  v-else
                  style="color: #6c757d; font-style: italic;"
                >
                  {{ $t('Maximum allowed is 60 minutes (1 hour).') }}
                </small>
              </b-col>
            </b-row>

            <b-row class="match-height mt-1">
              <b-col md="6">
                <b-card class="card-format">
                  <h6 class="section-label text-size mb-1 mt-0">
                    {{ $t('Logo on Document') }}
                  </h6>
                  <Spinner :show="logoImageFile.loading" />
                  <div class="upload-image">
                    <b-form-group>
                      <b-form-file
                        id="upload-logo"
                        v-model="logoImageFile.file"
                        :placeholder="$t('Choose a file or drop it here...')"
                        :drop-placeholder="$t('Drop file here...')"
                        accept="image/jpeg, image/png, image/jpg"
                        plain
                        :disabled="isReadonly"
                        @change="handleFileUpload($event, 'logoImageFile')"
                      />
                      <div
                        v-if="!logoImageFile.url"
                        class="upload-instruction"
                      >
                        <h4>{{ $t('Upload Logo on Document') }}</h4>
                        <p>
                          {{
                            $t(
                              'Drag & drop file or choose from your device to upload'
                            )
                          }}
                        </p>
                        <p class="format">
                          {{ $t('Format accepted (jpg, png, jpeg)') }}
                        </p>
                      </div>

                      <b-img
                        v-if="logoImageFile.url"
                        :src="logoImageFile.url"
                        block
                        fluid
                        rounded
                      />
                      <div
                        v-if="logoImageFile.url && !isReadonly"
                        class="trash"
                      >
                        <feather-icon
                          icon="Trash2Icon"
                          size="20"
                          class="text-primary"
                          @click="clearImageFile('logoImageFile')"
                        />
                      </div>
                    </b-form-group>
                  </div>
                </b-card>
              </b-col>
              <b-col md="6">
                <b-card class="card-format">
                  <h6 class="section-label text-size mb-1 mt-0">
                    {{ $t('Login Background Image') }}
                  </h6>
                  <Spinner :show="loginBackgroundImageFile.loading" />
                  <div class="upload-image">
                    <b-form-group>
                      <b-form-file
                        id="upload-banner"
                        v-model="loginBackgroundImageFile.file"
                        :placeholder="$t('Choose a file or drop it here...')"
                        :drop-placeholder="$t('Drop file here...')"
                        accept="image/jpeg, image/png, image/jpg"
                        plain
                        :disabled="isReadonly"
                        @change="
                          handleFileUpload($event, 'loginBackgroundImageFile')
                        "
                      />
                      <div
                        v-if="!loginBackgroundImageFile.url"
                        class="upload-instruction"
                      >
                        <h4>{{ $t('Upload Login Background Image') }}</h4>
                        <p>
                          {{
                            $t(
                              'Drag & drop file or choose from your device to upload'
                            )
                          }}
                        </p>
                        <p class="format">
                          {{ $t('Format accepted (jpg, png, jpeg)') }}
                        </p>
                      </div>

                      <b-img
                        v-if="loginBackgroundImageFile.url"
                        :src="loginBackgroundImageFile.url"
                        block
                        fluid
                        rounded
                      />
                      <div
                        v-if="loginBackgroundImageFile.url && !isReadonly"
                        class="trash"
                      >
                        <feather-icon
                          icon="Trash2Icon"
                          size="20"
                          class="text-primary"
                          @click="clearImageFile('loginBackgroundImageFile')"
                        />
                      </div>
                    </b-form-group>
                  </div>
                </b-card>
              </b-col>
            </b-row>

            <!-- Official Digital Stamp -->
            <b-row class="match-height mb-2 mt-1">
              <b-col md="6">
                <b-card class="card-format">
                  <h6 class="section-label text-size mb-1 mt-0">
                    {{ $t('Official Digital Stamp') }}
                  </h6>
                  <Spinner :show="stampImageFile.loading" />
                  <div class="upload-image">
                    <b-form-group>
                      <b-form-file
                        id="upload-stamp"
                        v-model="stampImageFile.file"
                        :placeholder="$t('Choose a file or drop it here...')"
                        :drop-placeholder="$t('Drop file here...')"
                        accept="image/jpeg, image/png, image/jpg"
                        plain
                        :disabled="isReadonly"
                        @change="handleFileUpload($event, 'stampImageFile')"
                      />
                      <div
                        v-if="!stampImageFile.url"
                        class="upload-instruction"
                      >
                        <h4>{{ $t("Upload Institution's Stamp") }}</h4>
                        <p>
                          {{
                            $t(
                              'Drag & drop file or choose from your device to upload'
                            )
                          }}
                        </p>
                        <p class="format">
                          {{ $t(' Format accepted (jpg, png, jpeg)') }}
                        </p>
                        <p class="format">
                          {{ $t('File Maximum Size: 10 MB') }}
                        </p>
                      </div>

                      <b-img
                        v-if="stampImageFile.url"
                        :src="stampImageFile.url"
                        block
                        fluid
                        rounded
                      />
                      <div
                        v-if="stampImageFile.url && !isReadonly"
                        class="trash"
                      >
                        <feather-icon
                          icon="Trash2Icon"
                          size="20"
                          class="text-primary"
                          @click="clearImageFile('stampImageFile')"
                        />
                      </div>
                    </b-form-group>
                  </div>
                </b-card>
              </b-col>
            </b-row>
            <!-- Official Digital Stamp -->
            <div class="text-center mb-5">
              <Button
                v-if="getUserPermission('MODIFY_SYSTEM_SETUP')"
                :label="buttonLabel"
                :loading="isLoading"
                :disabled="isDisabled"
                size="lg"
                @click="saveInstitutionFileUpload()"
              />
            </div>
          </b-form>
        </validation-observer>
      </b-tab>

      <b-tab
        :title="$t('Signatures')"
      >
        <validation-observer ref="uploadDigitalRules">
          <b-form>
            <h6 class="section-label mb-3 mt-1">
              {{ $t('Digital Signature') }}
            </h6>
            <!-- FD Authorized Signature -->
            <b-row>
              <b-col md="6">
                <b-card class="card-format">
                  <h6 class="section-label text-size mb-1 mt-0">
                    {{ $t('FD Authorized Signature 1') }}
                  </h6>
                  <Spinner :show="fdPrimarySignature.loading" />
                  <div class="upload-image">
                    <b-form-group>
                      <b-form-file
                        id="upload-banner"
                        v-model="fdPrimarySignature.file"
                        :placeholder="$t('Choose a file or drop it here...')"
                        :drop-placeholder="$t('Drop file here...')"
                        accept="image/jpeg, image/png, image/jpg"
                        plain
                        :disabled="isReadonlyDigital"
                        @change="handleFileUpload($event, 'fdPrimarySignature')"
                      />
                      <div
                        v-if="!fdPrimarySignature.url"
                        class="upload-instruction"
                      >
                        <h4>{{ $t('Upload FD Authorized Signature') }}</h4>
                        <p>
                          {{ $t('Drag & drop file or choose from your device to upload') }}
                        </p>
                        <p class="format">
                          {{ $t('Format accepted (jpg, png, jpeg)') }}
                        </p>
                        <p class="format">
                          {{ $t('File Maximum Size: 10 MB') }}
                        </p>
                      </div>

                      <b-img
                        v-if="fdPrimarySignature.url"
                        :src="fdPrimarySignature.url"
                        block
                        fluid
                        rounded
                      />
                      <div
                        v-if="fdPrimarySignature.url && !isReadonlyDigital"
                        class="trash"
                      >
                        <feather-icon
                          icon="Trash2Icon"
                          size="20"
                          class="text-primary"
                          @click="deleteSignature('fdPrimarySignature')"
                        />
                      </div>
                    </b-form-group>
                  </div>

                  <b-row>
                    <b-col
                      md="12"
                      class="mt-2"
                    >
                      <ValidationObserver>
                        <InputField
                          id="fd-signer-name-1"
                          v-model="fdPrimarySignature.name"
                          :label="$t('Full Name of Signer')"
                          :placeholder="$t('Enter name of the signer')"
                          type="text"
                          :error-name="$t('Full Name of Signer')"
                          :rules="fdPrimarySignature.url ? 'required' : ''"
                          :readonly="isReadonlyDigital"
                        />
                      </ValidationObserver>
                    </b-col>
                    <b-col md="12">
                      <ValidationObserver>
                        <InputField
                          id="fd-signer-position-1"
                          v-model="fdPrimarySignature.position"
                          :label="$t('Position of Signer')"
                          :placeholder="$t('Like CEO, MD..')"
                          type="text"
                          :error-name="$t('Position of Signer')"
                          :rules="fdPrimarySignature.url ? 'required' : ''"
                          :readonly="isReadonlyDigital"
                        />
                      </ValidationObserver>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>
              <b-col md="6">
                <b-card class="card-format">
                  <h6 class="section-label text-size mb-1 mt-0">
                    {{ $t('FD Authorized Signature 2') }}
                  </h6>
                  <Spinner :show="fdSecondarySignature.loading" />
                  <div class="upload-image">
                    <b-form-group>
                      <b-form-file
                        id="upload-banner"
                        v-model="fdSecondarySignature.file"
                        :placeholder="$t('Choose a file or drop it here...')"
                        :drop-placeholder="$t('Drop file here...')"
                        accept="image/jpeg, image/png, image/jpg"
                        plain
                        :disabled="isReadonlyDigital"
                        @change="handleFileUpload($event, 'fdSecondarySignature')"
                      />
                      <div
                        v-if="!fdSecondarySignature.url"
                        class="upload-instruction"
                      >
                        <h4>{{ $t('Upload FD Authorized Signature') }}</h4>
                        <p>{{ $t('Drag & drop file or choose from your device to upload') }}
                        </p>
                        <p class="format">
                          {{ $t('Format accepted (jpg, png, jpeg)') }}
                        </p>
                        <p class="format">
                          {{ $t('File Maximum Size: 10 MB') }}
                        </p>
                      </div>

                      <b-img
                        v-if="fdSecondarySignature.url"
                        :src="fdSecondarySignature.url"
                        block
                        fluid
                        rounded
                      />
                      <div
                        v-if="fdSecondarySignature.url && !isReadonlyDigital"
                        class="trash"
                      >
                        <feather-icon
                          icon="Trash2Icon"
                          size="20"
                          class="text-primary"
                          @click="deleteSignature('fdSecondarySignature')"
                        />
                      </div>
                    </b-form-group>
                  </div>
                  <b-row>
                    <b-col
                      md="12"
                      class="mt-2"
                    >
                      <ValidationObserver>
                        <InputField
                          id="fd-signer-name-2"
                          v-model="fdSecondarySignature.name"
                          :label="$t('Full Name of Signer')"
                          :placeholder="$t('Enter name of the signer')"
                          type="text"
                          :error-name="$t('Full Name of Signer')"
                          :rules="fdSecondarySignature.url ? 'required' : ''"
                          :readonly="isReadonlyDigital"
                        />
                      </ValidationObserver>
                    </b-col>
                    <b-col md="12">
                      <ValidationObserver>
                        <InputField
                          id="fd-signer-position-2"
                          v-model="fdSecondarySignature.position"
                          :label="$t('Position of Signer')"
                          :placeholder="$t('Like CEO, MD..')"
                          type="text"
                          :error-name="$t('Position of Signer')"
                          :rules="fdSecondarySignature.url ? 'required' : ''"
                          :readonly="isReadonlyDigital"
                        />
                      </ValidationObserver>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>
            </b-row>
            <!-- FD Authorized Signature  -->

            <!-- Loan Agreement Authorized Signature -->
            <b-row>
              <b-col md="6">
                <b-card class="card-format">
                  <h6 class="section-label text-size mb-1 mt-0">
                    {{ $t('Loan Agreement Authorized Signature 1') }}
                  </h6>
                  <Spinner :show="loanPrimarySignature.loading" />
                  <div class="upload-image">
                    <b-form-group>
                      <b-form-file
                        id="upload-banner"
                        v-model="loanPrimarySignature.file"
                        :placeholder="$t('Choose a file or drop it here...')"
                        :drop-placeholder="$t('Drop file here...')"
                        accept="image/jpeg, image/png, image/jpg"
                        plain
                        :disabled="isReadonlyDigital"
                        @change="handleFileUpload($event, 'loanPrimarySignature')"
                      />
                      <div
                        v-if="!loanPrimarySignature.url"
                        class="upload-instruction"
                      >
                        <h4>
                          {{ $t('Upload Loan Agreement Authorized Signature') }}
                        </h4>
                        <p>
                          {{
                            $t(
                              'Drag & drop file or choose from your device to upload'
                            )
                          }}
                        </p>
                        <p class="format">
                          {{ $t('Format accepted (jpg, png, jpeg)') }}
                        </p>
                        <p class="format">
                          {{ $t('File Maximum Size: 10 MB') }}
                        </p>
                      </div>

                      <b-img
                        v-if="loanPrimarySignature.url"
                        :src="loanPrimarySignature.url"
                        block
                        fluid
                        rounded
                      />
                      <div
                        v-if="loanPrimarySignature.url && !isReadonlyDigital"
                        class="trash"
                      >
                        <feather-icon
                          icon="Trash2Icon"
                          size="20"
                          class="text-primary"
                          @click="deleteSignature('loanPrimarySignature')"
                        />
                      </div>
                    </b-form-group>
                  </div>

                  <b-row>
                    <b-col
                      md="12"
                      class="mt-2"
                    >
                      <ValidationObserver>
                        <InputField
                          id="loan-signer-name-1"
                          v-model="loanPrimarySignature.name"
                          :label="$t('Full Name of Signer')"
                          :placeholder="$t('Enter name of the signer')"
                          type="text"
                          :error-name="$t('Full Name of Signer')"
                          :rules="loanPrimarySignature.url ? 'required' : ''"
                          :readonly="isReadonlyDigital"
                        />
                      </ValidationObserver>
                    </b-col>
                    <b-col md="12">
                      <ValidationObserver>
                        <InputField
                          id="loan-signer-position-1"
                          v-model="loanPrimarySignature.position"
                          :label="$t('Position of Signer')"
                          :placeholder="$t('Like CEO, MD..')"
                          type="text"
                          :error-name="$t('Position of Signer')"
                          :rules="loanPrimarySignature.url ? 'required' : ''"
                          :readonly="isReadonlyDigital"
                        />
                      </ValidationObserver>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>
              <b-col md="6">
                <b-card class="card-format">
                  <h6 class="section-label text-size mb-1 mt-0">
                    {{ $t('Loan Agreement Authorized Signature 2') }}
                  </h6>
                  <Spinner :show="loanSecondarySignature.loading" />
                  <div class="upload-image">
                    <b-form-group>
                      <b-form-file
                        id="upload-banner"
                        v-model="loanSecondarySignature.file"
                        :placeholder="$t('Choose a file or drop it here...')"
                        :drop-placeholder="$t('Drop file here...')"
                        accept="image/jpeg, image/png, image/jpg"
                        plain
                        :disabled="isReadonlyDigital"
                        @change="handleFileUpload($event, 'loanSecondarySignature')"
                      />
                      <div
                        v-if="!loanSecondarySignature.url"
                        class="upload-instruction"
                      >
                        <h4>
                          {{ $t('Upload Loan Agreement Authorized Signature') }}
                        </h4>
                        <p>
                          {{
                            $t(
                              'Drag & drop file or choose from your device to upload'
                            )
                          }}
                        </p>
                        <p class="format">
                          {{ $t('Format accepted (jpg, png, jpeg)') }}
                        </p>
                        <p class="format">
                          {{ $t('File Maximum Size: 10 MB') }}
                        </p>
                      </div>

                      <b-img
                        v-if="loanSecondarySignature.url"
                        :src="loanSecondarySignature.url"
                        block
                        fluid
                        rounded
                      />
                      <div
                        v-if="loanSecondarySignature.url && !isReadonlyDigital"
                        class="trash"
                      >
                        <feather-icon
                          icon="Trash2Icon"
                          size="20"
                          class="text-primary"
                          @click="deleteSignature('loanSecondarySignature')"
                        />
                      </div>
                    </b-form-group>
                  </div>
                  <b-row>
                    <b-col
                      md="12"
                      class="mt-2"
                    >
                      <ValidationObserver>
                        <InputField
                          id="fd-signer-name-2"
                          v-model="loanSecondarySignature.name"
                          :label="$t('Full Name of Signer')"
                          :placeholder="$t('Enter name of the signer')"
                          type="text"
                          :error-name="$t('Full Name of Signer')"
                          :rules="loanSecondarySignature.url ? 'required' : ''"
                          :readonly="isReadonlyDigital"
                        />
                      </ValidationObserver>
                    </b-col>
                    <b-col md="12">
                      <ValidationObserver>
                        <InputField
                          id="fd-signer-position-2"
                          v-model="loanSecondarySignature.position"
                          :label="$t('Position of Signer')"
                          :placeholder="$t('Like CEO, MD..')"
                          type="text"
                          :error-name="$t('Position of Signer')"
                          :rules="loanSecondarySignature.url ? 'required' : ''"
                          :readonly="isReadonlyDigital"
                        />
                      </ValidationObserver>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>
            </b-row>
            <!-- Loan Agreement Authorized Signature -->
            <div class="text-center">
              <Button
                v-if="getUserPermission('MODIFY_SYSTEM_SETUP')"
                :label="buttonLabelDigital"
                :loading="isLoading"
                :disabled="isDisabled"
                size="lg"
                @click="saveSignatoryFileUpload()"
              />
            </div>
          </b-form>
        </validation-observer>
      </b-tab>
      <b-tab
        :title="$t('Official Regulatory')"
      >
        <validation-observer ref="regulatoryFooterRules">
          <b-form>
            <h6 class="section-label mb-3 mt-1">
              {{ $t('Regulatory Reference') }}
            </h6>
            <b-row>
              <b-col
                class="mb-2"
                md="8"
              >
                <InputField
                  id="regulatory-approval"
                  v-model="regulatoryFormData.regulatoryFooter"
                  :label="$t('Regulatory Approval')"
                  :placeholder="$t('Enter regulatory approval details')"
                  type="text"
                  :readonly="isReadonlyRegulatoryFooter"
                />
                <small
                  style="color: #6c757d; font-style: italic;"
                >
                  {{ $t('To be displayed on the footer of printed receipts.') }}
                </small>
              </b-col>

              <!-- Licensed by the Central Bank of Nigeria and insured by the NDIC -->
            </b-row>
            <div class="text-center">
              <Button
                v-if="getUserPermission('MODIFY_SYSTEM_SETUP')"
                :label="buttonLabelRegulatoryFooter"
                :loading="isLoading"
                :disabled="isDisabled"
                size="lg"
                @click="saveRegulatoryFooter"
              />
            </div>
          </b-form>
        </validation-observer>
      </b-tab>
    </b-tabs>
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTabs,
  BTab,
  BForm,
  BImg,
  BFormGroup,
  BFormFile,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver } from 'vee-validate'
import { mapActions, mapGetters } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { toastAlert } from '@core/mixins/toast'
import { required, positive } from '@validations'
import { permissionMixin } from '@core/mixins/permission'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import Spinner from '@/views/components/spinner/Spinner.vue'
import Button from '../../../../components/button/Button.vue'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import { operationPermissions } from '../../../../../helpers/permissions'
import {
  preferredFonts,
  preferredLoginOptions,
} from '../../../../../helpers/data'

export default {
  components: {
    Button,
    FullScreenSpinner,
    Spinner,
    BRow,
    BCol,
    BCard,
    BTabs,
    BTab,
    BForm,
    ValidationObserver,
    BImg,
    BFormGroup,
    BFormFile,
    InputField,
    SelectField,
  },
  directives: {
    Ripple,
  },
  mixins: [sweetAlert, toastAlert, permissionMixin],
  data() {
    return {
      required,
      positive,
      isDisabled: false,
      isReadonly: true,
      isReadonlyDigital: true,
      isReadonlyRegulatoryFooter: true,
      isEditable: true,
      isLoading: false,
      logoLoadSpinner: false,
      loginBannerLoadSpinner: false,
      stampLoadSpinner: false,
      signatureLoadSpinner: false,
      signatureLoadSpinner2: false,
      showSpinner: false,
      buttonLabel: this.$t('Edit'),
      buttonLabelDigital: this.$t('Edit'),
      buttonLabelRegulatoryFooter: this.$t('Edit'),
      preferredFonts,
      preferredLoginOptions,
      imageFormData: {
        primaryColor: '',
        secondaryColor: '',
      },

      logoImageFile: {
        file: null,
        url: '',
        loading: false,
      },
      loginBackgroundImageFile: {
        file: null,
        url: '',
        loading: false,
      },
      stampImageFile: {
        file: null,
        url: '',
        loading: false,
      },

      fdPrimarySignature: {
        file: null,
        url: '',
        loading: false,
        name: '',
        position: '',
        id: null,
        type: 'FDCertificate',
      },
      fdSecondarySignature: {
        file: null,
        url: '',
        loading: false,
        name: '',
        position: '',
        id: '',
        type: 'FDCertificate',
      },
      loanPrimarySignature: {
        file: null,
        url: '',
        loading: false,
        name: '',
        position: '',
        id: '',
        type: 'LoanAgreement',
      },
      loanSecondarySignature: {
        file: null,
        url: '',
        loading: false,
        name: '',
        position: '',
        id: '',
        type: 'LoanAgreement',
      },
      configurationFormData: {
        fontFamily: 'Inter',
        sessionTimeout: '',
      },
      sessionTimeoutError: '',
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        ariaDateFormat: 'Y-m-d',
      },
      regulatoryFormData: {
        regulatoryFooter: '',
      },
    }
  },
  computed: {
    ...mapGetters({
      token: 'auth/getToken',
    }),
  },
  mounted() {
    this.fetchInstitutionFileUpload()
    this.fetchDigitalSignatureFiles()
  },
  methods: {
    ...mapActions({
      getInstitutionFileUpload: 'operations/getInstitutionFileUploadList',
      updateInstitutionFileUpload: 'operations/updateInstitutionFileUploadAction',
      getDigitalSignatureFiles: 'operations/getDigitalSignatureFiles',
      updateSignatureFileUpload: 'operations/updateSignatureFileUpload',
      uploadImageFile: 'account/uploadImageFileAction',
      setInstitution: 'auth/setInstitution',
      deleteDocumentsUpload: 'operations/deleteDocumentsUpload',
    }),
    refreshInstitutionData() {
      // Invalidate cache so getInstitution fetches fresh data
      localStorage.removeItem('udara360_institution_cache')
      // Non-blocking dispatch — updates store + localStorage in background
      this.$store.dispatch('auth/getInstitution', window.location.host).catch(() => {})
    },
    validateSessionTimeout(value) {
      const num = Number(value)
      if (!value && value !== 0) {
        this.sessionTimeoutError = ''
        return
      }
      if (num === 0) {
        this.sessionTimeoutError = this.$t('Session timeout cannot be zero.')
      } else if (num < 1) {
        this.sessionTimeoutError = this.$t('Session timeout must be at least 1 minute.')
      } else if (num > 60) {
        this.sessionTimeoutError = this.$t('Session timeout cannot exceed 60 minutes.')
      } else {
        this.sessionTimeoutError = ''
      }
    },
    clearLogoImage() {
      this.logoImage = null
    },
    clearBannerImage() {
      this.bannerImage = null
    },
    clearStampImage() {
      this.stampImage = null
      this.hasStampImage = false
    },
    async handleFileUpload(event, fileObjectPath) {
      const documentFilePath = this[fileObjectPath]

      const { files } = event.target
      const file = files[0]

      const maxSizeMB = 10
      if (file.size > maxSizeMB * 1024 * 1024) {
        const limitText = this.$t('File size exceeds the limit of')
        this.alertError({ text: `${limitText} ${maxSizeMB} MB.` })
        this.clearImageFile(documentFilePath)
        return
      }

      try {
        documentFilePath.loading = true
        const formData = new FormData()
        formData.append('attachedFile', file)
        formData.append('type', 13)
        formData.append('referenceNumber', uuidv4())

        const response = await this.uploadImageFile({
          formData,
          permission: operationPermissions.MODIFY_SYSTEM_SETUP,
        })

        const { status, data } = response
        if (status === 200 && data) {
          documentFilePath.url = data.url
          documentFilePath.loading = false

          this.toastAlert({
            title: this.$t('Document uploaded successfully'),
          })
        }
      } catch (error) {
        error.response.data.errors.forEach(err => {
          this.alertError({
            text: `Upload failed: ${err.errorMessage}`,
          })
        })
      } finally {
        documentFilePath.loading = false
      }
    },

    clearImageFile(filePath) {
      const documentFilePath = this[filePath]
      documentFilePath.url = ''
      documentFilePath.loading = false
      documentFilePath.file = null
    },

    // Function to generate an empty GUID
    generateEmptyGUID() {
      return '00000000-0000-0000-0000-000000000000'
    },
    saveSignatoryFileUpload() {
      if (this.buttonLabelDigital === this.$t('Save Changes')) {
        this.$refs.uploadDigitalRules.validate().then(success => {
          if (success) {
            this.confirm(this.handleDigitalSubmit, {
              text: this.$t(
                'Are you sure you want to Update this Institution Digital Signature?',
              ),
            })
          }
        })
      } else {
        this.isReadonlyDigital = false
        this.isEditableDigital = true
        this.buttonLabelDigital = this.$t('Save Changes')
      }
    },
    async handleDigitalSubmit() {
      try {
        this.isLoadingDigital = true
        this.isDisabledDigital = true
        const formData = {
          signatories: [
            {
              signatureURL: this.fdPrimarySignature.url,
              signatureType: 'Primary',
              documentName: 'FD Certificate',
              ownerName: this.fdPrimarySignature.name,
              position: this.fdPrimarySignature.position,
              documentType: this.fdPrimarySignature.type,
              id: this.fdPrimarySignature.id,
            },
            {
              signatureURL: this.fdSecondarySignature.url,
              signatureType: 'Secondary',
              documentName: 'FD Certificate',
              ownerName: this.fdSecondarySignature.name,
              position: this.fdSecondarySignature.position,
              documentType: this.fdSecondarySignature.type,
            },
            {
              signatureURL: this.loanPrimarySignature.url,
              signatureType: 'Primary',
              documentName: 'Loan Application Form',
              ownerName: this.loanPrimarySignature.name,
              position: this.loanPrimarySignature.position,
              documentType: this.loanPrimarySignature.type,
            },
            {
              signatureURL: this.loanSecondarySignature.url,
              signatureType: 'Secondary',
              documentName: 'Loan Application Form',
              ownerName: this.loanSecondarySignature.name,
              position: this.loanSecondarySignature.position,
              documentType: this.loanSecondarySignature.type,
            },
          ],
        }
        const response = await this.updateSignatureFileUpload({
          formData,
          permission: operationPermissions.MODIFY_SYSTEM_SETUP,
        })
        const { status, data } = await response
        if (status === 200 && data) {
          this.alertSuccess({
            text: this.$t(
              'Your request to modify Digital Signature was successful',
            ),
          })
          this.fetchDigitalSignatureFiles()
          this.refreshInstitutionData()
          this.isReadonlyDigital = true
          this.isEditableDigital = true
          this.isDisabledDigital = false
          this.isLoadingDigital = false
          this.buttonLabelDigital = 'Edit'
        }
      } catch (error) {
        const requestText = this.$t(
          'Your request to Update Digital Signature failed. Reason:',
        )
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `${requestText} ${responseError.errorMessage}`,
          })
        })
      } finally {
        this.isEditable = true
        this.isDisabled = false
        this.isLoading = false
      }
    },
    saveInstitutionFileUpload() {
      if (this.buttonLabel === this.$t('Save Changes')) {
        const timeout = Number(this.configurationFormData.sessionTimeout)
        if (this.configurationFormData.sessionTimeout && (timeout <= 0 || timeout > 60)) {
          this.validateSessionTimeout(this.configurationFormData.sessionTimeout)
          return
        }
        this.$refs.uploadRules.validate().then(success => {
          if (success) {
            this.confirm(this.handleSubmit, {
              text: this.$t(
                'Are you sure you want to Update this Institution Information?',
              ),
            })
          }
        })
      } else {
        this.isReadonly = false
        this.isEditable = true
        this.buttonLabel = this.$t('Save Changes')
      }
    },

    async handleSubmit() {
      try {
        this.isLoading = true
        this.isDisabled = true
        const formData = {
          id: this.generateEmptyGUID(),
          details: {
            logoURL: this.logoImageFile.url,
            loginPageBackgroundURL: this.loginBackgroundImageFile.url,
            stampURL: this.stampImageFile.url,
            secondaryColor: this.imageFormData.secondaryColor,
            primaryColor: this.imageFormData.primaryColor,
            preferredFont: this.configurationFormData.fontFamily,
          },
          configuration: {
            sessionTimeout: Number(this.configurationFormData.sessionTimeout),
          },
        }
        const response = await this.updateInstitutionFileUpload({
          formData,
          permission: operationPermissions.MODIFY_SYSTEM_SETUP,
        })
        const { status, data } = await response
        if (status === 200 && data) {
          this.alertSuccess({
            text: this.$t(
              'Your request to modify Institution Information was successful',
            ),
          })
          document.documentElement.style.setProperty(
            '--font-family-primary',
            `'${this.configurationFormData.fontFamily}', sans-serif`,
            'important',
          )
          this.setInstitution({
            ...this.$store.getters['auth/getInstitution'],
            preferredFont: this.configurationFormData.fontFamily,
          })
          this.fetchInstitutionFileUpload()
          this.refreshInstitutionData()
          this.isReadonly = true
          this.isEditable = true
          this.isDisabled = false
          this.isLoading = false
          this.buttonLabel = 'Edit'
        }
      } catch (error) {
        const requestText = this.$t(
          'Your request to Update Institution Information failed. Reason:',
        )
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `${requestText} ${responseError.errorMessage}`,
          })
        })
      } finally {
        this.isEditable = true
        this.isDisabled = false
        this.isLoading = false
      }
    },
    saveRegulatoryFooter() {
      if (this.buttonLabelRegulatoryFooter === this.$t('Save Changes')) {
        this.$refs.regulatoryFooterRules.validate().then(success => {
          if (success) {
            this.confirm(this.handleRegulatorySubmit, {
              text: this.$t(
                'Are you sure you want to Update this Official Regulatory?',
              ),
            })
          }
        })
      } else {
        this.isReadonlyRegulatoryFooter = false
        this.isEditable = true
        this.buttonLabelRegulatoryFooter = this.$t('Save Changes')
      }
    },
    async handleRegulatorySubmit() {
      try {
        this.isLoading = true
        this.isDisabled = true
        const formData = {
          id: this.generateEmptyGUID(),
          details: {
            logoURL: this.logoImageFile.url,
            loginPageBackgroundURL: this.loginBackgroundImageFile.url,
            stampURL: this.stampImageFile.url,
            secondaryColor: this.imageFormData.secondaryColor,
            primaryColor: this.imageFormData.primaryColor,
            preferredFont: this.configurationFormData.fontFamily,
            regulatoryFooter: this.regulatoryFormData.regulatoryFooter,
          },
        }
        const response = await this.updateInstitutionFileUpload({
          formData,
          permission: operationPermissions.MODIFY_SYSTEM_SETUP,
        })
        const { status, data } = await response
        if (status === 200 && data) {
          this.alertSuccess({
            text: this.$t(
              'Your request to modify Official Regulatory Information was successful',
            ),
          })

          this.fetchInstitutionFileUpload()
          this.refreshInstitutionData()
          this.isReadonlyRegulatoryFooter = true
          this.isEditable = true
          this.isDisabled = false
          this.isLoading = false
          this.buttonLabelRegulatoryFooter = 'Edit'
        }
      } catch (error) {
        const requestText = this.$t(
          'Your request to Update Official Regulatory Information failed. Reason:',
        )
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `${requestText} ${responseError.errorMessage}`,
          })
        })
      } finally {
        this.isEditable = true
        this.isDisabled = false
        this.isLoading = false
      }
    },
    async fetchInstitutionFileUpload() {
      try {
        this.showSpinner = true
        const response = await this.getInstitutionFileUpload({
          permission: operationPermissions.VIEW_SYSTEM_SETUP,
        })
        const { status, data } = response
        if (status === 200 && data) {
          const binaryContent = new Uint8Array([0x25, 0x50, 0x44, 0x46, 0x2d])
          const fakeFile = new File([binaryContent], 'Document.pdf', {
            type: 'application/pdf',
            lastModified: new Date().getTime(),
          })
          this.logoImageFile = {
            file: fakeFile,
            url: data.logoURL,
            loading: false,
          }
          this.loginBackgroundImageFile = {
            file: fakeFile,
            url: data.loginPageBackgroundURL,
            loading: false,
          }
          this.stampImageFile = {
            file: fakeFile,
            url: data.stampUrl,
            loading: false,
          }
          this.imageFormData.secondaryColor = data.secondaryColor
          this.imageFormData.primaryColor = data.primaryColor
          this.configurationFormData.fontFamily = data.preferredFont || 'Inter'
          this.configurationFormData.sessionTimeout = data.sessionTimeout || ''
          this.regulatoryFormData.regulatoryFooter = data.regulatoryFooter || ''
          this.showSpinner = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async fetchDigitalSignatureFiles() {
      try {
        this.showSpinner = true
        const response = await this.getDigitalSignatureFiles({
          permission: operationPermissions.VIEW_SYSTEM_SETUP,
        })
        const { status, data } = response
        if (status === 200 && data) {
          const binaryContent = new Uint8Array([0x25, 0x50, 0x44, 0x46, 0x2d])
          const fakeFile = new File([binaryContent], 'Document.pdf', {
            type: 'application/pdf',
            lastModified: new Date().getTime(),
          })

          data.forEach(item => {
            if (item.signatureURL) {
              const mappedSignature = {
                file: fakeFile,
                url: item.signatureURL,
                name: item.ownerName,
                position: item.position,
                type: item.documentType,
                loading: false,
                id: item.id,
              }

              if (
                item.documentType === 'FDCertificate'
                && item.signatureType === 'Primary'
              ) {
                this.fdPrimarySignature = mappedSignature
              }
              if (
                item.documentType === 'FDCertificate'
                && item.signatureType === 'Secondary'
              ) {
                this.fdSecondarySignature = mappedSignature
              }
              if (
                item.documentType === 'LoanAgreement'
                && item.signatureType === 'Primary'
              ) {
                this.loanPrimarySignature = mappedSignature
              }
              if (
                item.documentType === 'LoanAgreement'
                && item.signatureType === 'Secondary'
              ) {
                this.loanSecondarySignature = mappedSignature
              }
            }
          })
          this.showSpinner = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async deleteSignature(fieldKey) {
      const field = this[fieldKey]

      if (!field.id || field.id === null || field.id === '') {
        this.clearImageFiles(fieldKey)
        return
      }

      const result = await this.$swal({
        title: this.$t('Delete signature?'),
        text: this.$t('This cannot be undone.'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Delete'),
        cancelButtonText: this.$t('Cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
        reverseButtons: false,
      })
      if (!result.isConfirmed) return

      try {
        field.loading = true

        // Use the query param approach that matches your Vuex action
        const response = await this.deleteDocumentsUpload({
          signatureId: field.id,
          permission: operationPermissions.MODIFY_SYSTEM_SETUP,
        })

        if (response.status === 200) {
          this.clearImageFiles(fieldKey)
          field.id = null
          field.name = ''
          field.position = ''
          this.alertSuccess({ text: this.$t('Signature deleted successfully') })
          // Refresh the list to get updated data
          await this.fetchDigitalSignatureFiles()
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        field.loading = false
      }
    },
    clearImageFiles(fieldKey) {
      const f = this[fieldKey]
      if (f && f.url) {
        // Only revoke if it's a blob URL (starts with 'blob:')
        if (f.url.startsWith('blob:')) {
          URL.revokeObjectURL(f.url)
        }
      }
      f.file = null
      f.url = ''
      f.name = ''
      f.position = ''
    },
  },
}
</script>

<style lang="scss" scoped>
.custom-checkbox:last-child {
  margin-left: 20px !important;
}

.upload-image {
  border: 2px dashed #dcd9d9;
  min-height: 250px;
  position: relative;
  width: 100%;
}

.format {
  color: #999999 !important;
}

form input[type='file'] {
  position: absolute;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  outline: none;
  opacity: 0;
}

.upload-instruction {
  padding-top: 22%;
  color: #000;
  text-align: center;
}

.trash {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-right: 10px;
  cursor: pointer;
}
.card-format {
  display: block !important;
  border: 3px solid var(--gray) !important;
  border-radius: 1rem !important;
  padding: 0.2rem !important;
}
.text-size {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--grey);
}
.error-message {
  margin-top: -1rem !important;
}
</style>

<style lang="scss" scoped>
@import '../../../../../@core/styles/global.css';
@import '@core/scss/vue/libs/vue-select.scss';
</style>
